import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { blockRegistry } from '@/lib/email-builder/block-registry';
import { BlockIcon } from './BlockIcon';
import type { EmailBlock } from '@/types/email-blocks';

interface OutlineNodeProps {
  block: EmailBlock;
  depth: number;
  selectedBlockId: string | null;
  onSelect: (id: string | null) => void;
}

function OutlineNode({ block, depth, selectedBlockId, onSelect }: OutlineNodeProps) {
  const isSelected = block.id === selectedBlockId;
  const registryEntry = blockRegistry.get(block.blockType);
  const displayName = registryEntry?.displayName ?? block.blockType;
  const hasChildren = block.childBlocks && block.childBlocks.length > 0;

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(block.id)}
        className={`
          w-full flex items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs transition-colors
          ${isSelected
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-accent hover:text-accent-foreground'
          }
        `}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        title={block.blockType}
      >
        {/* Tree connector line indicator */}
        {depth > 0 && (
          <span className={`flex-shrink-0 text-muted-foreground ${isSelected ? 'text-primary-foreground/60' : ''}`}>
            {'—'}
          </span>
        )}
        <span className={`flex-shrink-0 ${isSelected ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
          <BlockIcon iconName={block.blockType} className="h-3 w-3" />
        </span>
        <span className="truncate font-medium">{displayName}</span>
      </button>

      {hasChildren && (
        <div>
          {block.childBlocks.map((child) => (
            <OutlineNode
              key={child.id}
              block={child}
              depth={depth + 1}
              selectedBlockId={selectedBlockId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Document Outline Panel
 *
 * Displays the full block tree of the current document as a
 * hierarchical list. Clicking any item selects that block;
 * clicking "Document" clears the selection (shows document properties).
 */
export function DocumentOutlinePanel() {
  const { blockTree, selectedBlockId, selectBlock } = useEmailBuilderStore();

  const isDocumentSelected = selectedBlockId === null;

  if (blockTree.length === 0) {
    return (
      <div className="space-y-1">
        {/* Document root row */}
        <button
          type="button"
          onClick={() => selectBlock(null)}
          className={`
            w-full flex items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs font-semibold transition-colors
            ${isDocumentSelected
              ? 'bg-primary text-primary-foreground'
              : 'text-foreground hover:bg-accent hover:text-accent-foreground'
            }
          `}
        >
          <span className={`flex-shrink-0 ${isDocumentSelected ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </span>
          <span>Document</span>
        </button>
        <p className="px-2 py-3 text-xs text-muted-foreground text-center">
          No blocks yet. Drag blocks from the Blocks tab to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {/* Document root row */}
      <button
        type="button"
        onClick={() => selectBlock(null)}
        className={`
          w-full flex items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs font-semibold transition-colors
          ${isDocumentSelected
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-accent hover:text-accent-foreground'
          }
        `}
      >
        <span className={`flex-shrink-0 ${isDocumentSelected ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </span>
        <span>Document</span>
      </button>

      {/* Block tree */}
      {blockTree.map((block) => (
        <OutlineNode
          key={block.id}
          block={block}
          depth={1}
          selectedBlockId={selectedBlockId}
          onSelect={selectBlock}
        />
      ))}
    </div>
  );
}
